import { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";

/**
 * Type OPC : colonne `opc_type` sur `public.profiles` — valeurs `mandate` | `independant`.
 * Ex. : ALTER TABLE public.profiles ADD COLUMN IF NOT EXISTS opc_type text;
 */

const BG = "#F8F4EE";
const BG_CARD = "#FFFFFF";
const GOLD = "#C09A5B";
const TAUPE = "#3D2E20";
const MUTED = "#8B7361";
const BORDER = "#D8C9B8";
const VERT = "#5C8A5C";
const BLEU = "#4A7A9B";
const BEIGE = "#EDE4D6";
const ROUGE = "#B85C3A";

const FF_PLAYFAIR = "'Playfair Display', serif";
const FF_DM = "'DM Sans', sans-serif";

const ROLES = [
  { value: "admin", label: "Administrateur" },
  { value: "opc", label: "OPC" },
  { value: "chef_chantier", label: "Chef de Chantier" },
  { value: "entreprise", label: "Entreprise" },
  { value: "client", label: "Client" },
];

/** Valeurs persistées : profiles.opc_type */
const OPC_TYPE_OPTIONS = [
  { value: "mandate", label: "OPC Mandaté", hint: "accès limité par l'admin" },
  { value: "independant", label: "OPC Indépendant", hint: "abonnement propre" },
];

function normalizeOpcType(v) {
  return v === "independant" ? "independant" : "mandate";
}

function opcSubLabel(v) {
  return normalizeOpcType(v) === "independant" ? "OPC Indépendant" : "OPC Mandaté";
}

function RoleBadge({ role, opcType }) {
  const label = ROLES.find((r) => r.value === role)?.label || role;

  const pill = {
    display: "inline-block",
    borderRadius: 999,
    padding: "5px 12px",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.03em",
    fontFamily: FF_DM,
  };

  if (role === "admin") {
    return <span style={{ ...pill, background: TAUPE, color: "#FFF" }}>{label}</span>;
  }
  if (role === "opc") {
    const sub = opcSubLabel(opcType);
    return (
      <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
        <span style={{ ...pill, background: GOLD, color: "#FFF" }}>OPC</span>
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: MUTED,
            letterSpacing: "0.02em",
            fontFamily: FF_DM,
            lineHeight: 1.2,
          }}
        >
          {sub}
        </span>
      </span>
    );
  }
  if (role === "chef_chantier") {
    return <span style={{ ...pill, background: BLEU, color: "#FFF" }}>{label}</span>;
  }
  if (role === "entreprise") {
    return <span style={{ ...pill, background: VERT, color: "#FFF" }}>{label}</span>;
  }
  if (role === "client") {
    return <span style={{ ...pill, background: BEIGE, color: TAUPE, border: `1px solid ${BORDER}` }}>{label}</span>;
  }
  return <span style={{ ...pill, background: "#EEE", color: TAUPE }}>{label}</span>;
}

const inputStyle = {
  width: "100%",
  padding: "11px 14px",
  border: `1px solid ${BORDER}`,
  borderRadius: 10,
  fontSize: 14,
  color: TAUPE,
  background: BG_CARD,
  boxSizing: "border-box",
  outline: "none",
  fontFamily: FF_DM,
};

const labelStyle = {
  display: "block",
  fontSize: 11,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: MUTED,
  marginBottom: 6,
  fontWeight: 600,
  fontFamily: FF_DM,
};

const EMPTY_FORM = {
  email: "",
  password: "",
  full_name: "",
  role: "chef_chantier",
  opc_type: "mandate",
};

export default function UsersManagementPage({ cabinetId = null }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editUser, setEditUser] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [companyName, setCompanyName] = useState("MON CABINET");

  const [allProjects, setAllProjects] = useState([]);
  const [formOpcProjectIds, setFormOpcProjectIds] = useState([]);
  const [opcModalUser, setOpcModalUser] = useState(null);
  const [opcModalIds, setOpcModalIds] = useState([]);
  const [opcModalOpcType, setOpcModalOpcType] = useState("mandate");
  const [savingOpc, setSavingOpc] = useState(false);

  useEffect(() => {
    try {
      const n = localStorage.getItem("yoma_company_name");
      if (n) setCompanyName(n);
    } catch (_) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (cabinetId == null) return;
    fetchUsers();
  }, [cabinetId]);

  useEffect(() => {
    if (cabinetId == null) return;
    (async () => {
      const { data } = await supabase.from("projects").select("id, name").eq("cabinet_id", cabinetId).order("name");
      setAllProjects(data || []);
    })();
  }, [cabinetId]);

  async function syncOpcProjets(opcId, projectIds) {
    await supabase.from("opc_projets").delete().eq("opc_id", opcId).eq("cabinet_id", cabinetId);
    if (!projectIds?.length) return;
    const rows = projectIds.map((project_id) => ({ opc_id: opcId, project_id, cabinet_id: cabinetId }));
    const { error } = await supabase.from("opc_projets").insert(rows);
    if (error) console.error("opc_projets:", error);
  }

  async function openOpcModal(u) {
    if (u.role !== "opc") return;
    setOpcModalUser(u);
    setOpcModalOpcType(normalizeOpcType(u.opc_type));
    const { data } = await supabase.from("opc_projets").select("project_id").eq("opc_id", u.id).eq("cabinet_id", cabinetId);
    setOpcModalIds((data || []).map((r) => r.project_id));
  }

  async function saveOpcModal() {
    if (!opcModalUser) return;
    setSavingOpc(true);
    const { error: profErr } = await supabase
      .from("profiles")
      .update({ opc_type: opcModalOpcType })
      .eq("id", opcModalUser.id)
      .eq("cabinet_id", cabinetId);
    if (profErr) console.error("profiles opc_type:", profErr);

    // Synchroniser aussi côté table métier `user_profiles`.
    {
      const selectedRole = opcModalUser.role;
      const opcType = opcModalOpcType;
      const { error: upsertErr } = await supabase.from("user_profiles").upsert(
        {
          id: opcModalUser.id,
          role: selectedRole,
          opc_type: selectedRole === "opc" ? opcType : null,
        },
        { onConflict: "id" }
      );
      if (upsertErr) console.error("user_profiles:", upsertErr);
    }

    if (opcModalOpcType === "mandate") {
      await syncOpcProjets(opcModalUser.id, opcModalIds);
    } else {
      await syncOpcProjets(opcModalUser.id, []);
    }
    setUsers((prev) =>
      prev.map((u) =>
        u.id === opcModalUser.id ? { ...u, opc_type: opcModalOpcType } : u
      )
    );
    setSavingOpc(false);
    setOpcModalUser(null);
  }

  function toggleFormOpcProject(pid) {
    setFormOpcProjectIds((prev) => (prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid]));
  }

  function toggleModalOpc(pid) {
    setOpcModalIds((prev) => (prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid]));
  }

  async function fetchUsers() {
    setLoading(true);
    const { data, error } = await supabase.from("profiles").select("*").eq("cabinet_id", cabinetId).order("created_at", { ascending: false });
    if (!error) setUsers(data || []);
    setLoading(false);
  }

  async function handleCreate() {
    if (!form.email.trim() || !form.password.trim() || !form.full_name.trim()) {
      setError("Tous les champs obligatoires doivent être remplis.");
      return;
    }
    if (form.password.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères.");
      return;
    }

    setSaving(true);
    setError("");

    await supabase.auth.refreshSession();
    const {
      data: { session },
    } = await supabase.auth.getSession();

    const response = await fetch("https://cdxlbduhgrtbvnmqzice.supabase.co/functions/v1/create-user", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        email: form.email.trim(),
        password: form.password,
        full_name: form.full_name.trim(),
        role: form.role,
      }),
    });

    const result = await response.json();

    if (result.error) {
      setError("Erreur : " + result.error);
      setSaving(false);
      return;
    }

    let newUserId = result.user?.id || result.user_id || result.id || result.profile?.id;
    if (!newUserId) {
      const { data: prof } = await supabase.from("profiles").select("id").eq("email", form.email.trim()).eq("cabinet_id", cabinetId).maybeSingle();
      newUserId = prof?.id;
    }

    // Persist aussi côté table métier `user_profiles` pour garder le rôle/opc_type en DB.
    if (newUserId) {
      const selectedRole = form.role;
      const opcType = form.opc_type;
      const { error: upsertErr } = await supabase.from("user_profiles").upsert(
        {
          id: newUserId,
          role: selectedRole,
          opc_type: selectedRole === "opc" ? opcType : null,
        },
        { onConflict: "id" }
      );
      if (upsertErr) console.error("user_profiles:", upsertErr);
    }

    if (newUserId) {
      const { error: profErr } = await supabase
        .from("profiles")
        .update({ opc_type: form.role === "opc" ? form.opc_type : null })
        .eq("id", newUserId);
      if (profErr) console.error("profiles opc_type:", profErr);
    }

    if (form.role === "opc" && newUserId) {
      if (form.opc_type === "mandate") {
        await syncOpcProjets(newUserId, formOpcProjectIds);
      } else {
        await syncOpcProjets(newUserId, []);
      }
    }

    setSuccess(`Compte créé pour ${form.full_name}.`);
    setForm({ ...EMPTY_FORM });
    setFormOpcProjectIds([]);
    setShowForm(false);
    setTimeout(() => setSuccess(""), 3000);
    fetchUsers();
    setSaving(false);
  }

  async function handleUpdateRole(userId, newRole) {
    const prevRole = users.find((u) => u.id === userId)?.role;
    const prevUser = users.find((u) => u.id === userId);
    const opcType = newRole === "opc" ? prevUser?.opc_type || "mandate" : null;
    const payload = { role: newRole };
    if (newRole !== "opc") payload.opc_type = null;
    await supabase.from("profiles").update(payload).eq("id", userId).eq("cabinet_id", cabinetId);

    // Synchroniser aussi la table métier `user_profiles` (id/role/opc_type).
    {
      const { error: upsertErr } = await supabase.from("user_profiles").upsert(
        {
          id: userId,
          role: newRole,
          opc_type: newRole === "opc" ? opcType : null,
        },
        { onConflict: "id" }
      );
      if (upsertErr) console.error("user_profiles:", upsertErr);
    }

    if (prevRole === "opc" && newRole !== "opc") {
      await supabase.from("opc_projets").delete().eq("opc_id", userId).eq("cabinet_id", cabinetId);
    }
    setUsers((prev) =>
      prev.map((u) =>
        u.id === userId ? { ...u, role: newRole, opc_type: newRole === "opc" ? u.opc_type : null } : u
      )
    );
    setEditUser(null);
  }

  async function handleDelete(userId) {
    const response = await fetch("https://cdxlbduhgrtbvnmqzice.supabase.co/functions/v1/delete-user", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId }),
    });
    const result = await response.json();
    if (!result.error) {
      setUsers((prev) => prev.filter((u) => u.id !== userId));
    }
    setConfirmDel(null);
  }

  const kpiTotal = users.length;
  const kpiActifs = useMemo(
    () => users.filter((u) => ["opc", "chef_chantier", "entreprise", "client"].includes(u.role)).length,
    [users]
  );
  const kpiRoles = useMemo(() => new Set(users.map((u) => u.role).filter(Boolean)).size, [users]);

  if (cabinetId == null) {
    return <div style={{ padding: 24, textAlign: "center", color: "#8B7361" }}>Chargement...</div>;
  }

  return (
    <><style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;700&family=DM+Sans:wght@400;500;600;700&display=swap');
        `}
      </style><div
        style={{
          fontFamily: FF_DM,
          color: TAUPE,
          background: BG,
          minHeight: "100%",
          padding: "28px 24px 48px",
          boxSizing: "border-box",
        }}
      ><div style={{ maxWidth: 1200, margin: "0 auto" }}>
          {/* HEADER */}
          <header
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 16,
              marginBottom: 28,
            }}
          ><div style={{ flex: "1 1 280px" }}><div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: MUTED,
                  marginBottom: 8,
                }}
              >
                {companyName}
              </div><h1
                style={{
                  margin: 0,
                  fontFamily: FF_PLAYFAIR,
                  fontSize: 32,
                  fontWeight: 700,
                  color: TAUPE,
                  lineHeight: 1.15,
                }}
              >
                Gestion des accès
              </h1><p style={{ margin: "10px 0 0", fontSize: 15, color: MUTED, maxWidth: 520 }}>
                Créez et gérez les comptes collaborateurs et clients
              </p></div><button
              type="button"
              onClick={() => {
                setShowForm(true);
                setForm({ ...EMPTY_FORM });
                setFormOpcProjectIds([]);
                setError("");
              }}
              style={{
                padding: "12px 22px",
                borderRadius: 10,
                border: "none",
                background: `linear-gradient(135deg, ${GOLD} 0%, #A8844A 100%)`,
                color: "#FFF",
                fontFamily: FF_DM,
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
                boxShadow: "0 4px 16px rgba(192,154,91,0.45)",
                alignSelf: "flex-start",
              }}
            >
              Nouvel utilisateur
            </button></header>

          {success && (
            <div
              style={{
                padding: "14px 18px",
                background: "rgba(92,138,92,0.12)",
                border: "1px solid rgba(92,138,92,0.35)",
                borderRadius: 12,
                fontSize: 14,
                color: VERT,
                marginBottom: 20,
                fontFamily: FF_DM,
                fontWeight: 600,
              }}
            >
              {success}
            </div>
          )}

          {/* KPIs */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 14,
              marginBottom: 28,
            }}
          ><div
              style={{
                background: BG_CARD,
                borderRadius: 14,
                padding: "18px 20px",
                border: `1px solid ${BORDER}`,
                boxShadow: "0 4px 20px rgba(61,46,32,0.06)",
              }}
            ><div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", color: MUTED, marginBottom: 6 }}>Total utilisateurs</div><div style={{ fontFamily: FF_PLAYFAIR, fontSize: 28, fontWeight: 700, color: TAUPE }}>{kpiTotal}</div></div><div
              style={{
                background: BG_CARD,
                borderRadius: 14,
                padding: "18px 20px",
                border: "1px solid rgba(92,138,92,0.35)",
                boxShadow: "0 4px 20px rgba(92,138,92,0.1)",
              }}
            ><div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", color: MUTED, marginBottom: 6 }}>Actifs</div><div style={{ fontFamily: FF_PLAYFAIR, fontSize: 28, fontWeight: 700, color: VERT }}>{kpiActifs}</div></div><div
              style={{
                background: BG_CARD,
                borderRadius: 14,
                padding: "18px 20px",
                border: "1px solid rgba(192,154,91,0.45)",
                boxShadow: "0 4px 20px rgba(192,154,91,0.12)",
              }}
            ><div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", color: MUTED, marginBottom: 6 }}>Rôles différents</div><div style={{ fontFamily: FF_PLAYFAIR, fontSize: 28, fontWeight: 700, color: GOLD }}>{kpiRoles}</div></div></div>

          {/* TABLEAU */}
          <section
            style={{
              background: BG_CARD,
              border: `1px solid ${BORDER}`,
              borderRadius: 16,
              padding: "22px 24px 8px",
              boxShadow: "0 6px 28px rgba(61,46,32,0.07)",
              marginBottom: 28,
            }}
          ><h2 style={{ margin: "0 0 18px", fontFamily: FF_PLAYFAIR, fontSize: 22, fontWeight: 700, color: TAUPE }}>Utilisateurs</h2>

            {loading ? (
              <div style={{ padding: "40px 0", textAlign: "center", color: MUTED, fontSize: 14 }}>Chargement…</div>
            ) : users.length === 0 ? (
              <div style={{ padding: "40px 0", textAlign: "center", color: MUTED, fontSize: 14, fontStyle: "italic" }}>Aucun utilisateur.</div>
            ) : (
              <div style={{ overflowX: "auto", marginBottom: 16 }}><table style={{ width: "100%", minWidth: 640, borderCollapse: "collapse" }}><thead><tr style={{ borderBottom: `1px solid ${BORDER}` }}>
                      {["Nom", "Email", "Rôle", "Créé le", "Actions"].map((h) => (
                        <th
                          key={h}
                          style={{
                            textAlign: "left",
                            padding: "10px 12px",
                            fontSize: 10,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            color: MUTED,
                            fontWeight: 700,
                            fontFamily: FF_DM,
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr></thead><tbody>
                    {users.map((u) => (
                      <tr key={u.id} style={{ borderBottom: `1px solid rgba(216,201,184,0.5)` }}><td style={{ padding: "14px 12px", fontSize: 15, fontWeight: 600, fontFamily: FF_DM, color: TAUPE }}>{u.full_name || "—"}</td><td style={{ padding: "14px 12px", fontSize: 14, color: MUTED, wordBreak: "break-word" }}>{u.email}</td><td style={{ padding: "14px 12px", verticalAlign: "middle" }}>
                          {editUser === u.id ? (
                            <select
                              value={u.role}
                              onChange={(e) => handleUpdateRole(u.id, e.target.value)}
                              style={{ ...inputStyle, width: "auto", minWidth: 180, padding: "8px 12px", cursor: "pointer" }}
                              autoFocus
                            >
                              {ROLES.map((r) => (
                                <option key={r.value} value={r.value}>
                                  {r.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <RoleBadge role={u.role} opcType={u.opc_type} />
                          )}
                        </td><td style={{ padding: "14px 12px", fontSize: 13, color: MUTED, whiteSpace: "nowrap" }}>
                          {new Date(u.created_at).toLocaleDateString("fr-FR")}
                        </td><td style={{ padding: "14px 12px" }}><div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}                            >{u.role === "opc" && (
                              <button
                                type="button"
                                onClick={() => openOpcModal(u)}
                                style={{
                                  padding: "7px 12px",
                                  fontSize: 12,
                                  fontWeight: 600,
                                  border: `1px solid rgba(192,154,91,0.5)`,
                                  borderRadius: 8,
                                  background: "rgba(192,154,91,0.12)",
                                  color: TAUPE,
                                  cursor: "pointer",
                                  fontFamily: FF_DM,
                                }}
                              >
                                Réglages OPC
                              </button>
                            )}<button
                              type="button"
                              onClick={() => setEditUser(editUser === u.id ? null : u.id)}
                              style={{
                                padding: "7px 12px",
                                fontSize: 12,
                                fontWeight: 600,
                                border: `1px solid ${BORDER}`,
                                borderRadius: 8,
                                background: "#FAF8F5",
                                color: TAUPE,
                                cursor: "pointer",
                                fontFamily: FF_DM,
                              }}
                            >
                              {editUser === u.id ? "Annuler" : "Modifier rôle"}
                            </button><button
                              type="button"
                              onClick={() => setConfirmDel(u)}
                              style={{
                                padding: "7px 12px",
                                fontSize: 12,
                                fontWeight: 600,
                                border: "1px solid rgba(184,92,58,0.3)",
                                borderRadius: 8,
                                background: "rgba(184,92,58,0.06)",
                                color: ROUGE,
                                cursor: "pointer",
                                fontFamily: FF_DM,
                              }}
                            >
                              Supprimer
                            </button></div></td></tr>
                    ))}
                  </tbody></table></div>
            )}
          </section>

          {/* NIVEAUX D'ACCÈS */}
          <section><h2 style={{ margin: "0 0 16px", fontFamily: FF_PLAYFAIR, fontSize: 22, fontWeight: 700, color: TAUPE }}>Niveaux d&apos;accès</h2><div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                gap: 16,
              }}
            ><div
                style={{
                  borderRadius: 14,
                  padding: "20px 22px",
                  background: TAUPE,
                  color: "#FFF",
                  border: `1px solid ${TAUPE}`,
                  boxShadow: "0 8px 28px rgba(61,46,32,0.2)",
                }}
              ><div style={{ fontFamily: FF_PLAYFAIR, fontSize: 18, fontWeight: 700, marginBottom: 10 }}>Administrateur</div><p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, opacity: 0.95 }}>Accès complet sans restriction</p></div><div
                style={{
                  borderRadius: 14,
                  padding: "20px 22px",
                  background: "rgba(192,154,91,0.18)",
                  color: TAUPE,
                  border: "1px solid rgba(192,154,91,0.4)",
                }}
              ><div style={{ fontFamily: FF_PLAYFAIR, fontSize: 18, fontWeight: 700, marginBottom: 10, color: TAUPE }}>OPC / Chef de Chantier</div><p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: TAUPE }}>
                  Projet, Jalons, PV Chantier, Kanban, Documents, Messagerie, Reporting
                </p></div><div
                style={{
                  borderRadius: 14,
                  padding: "20px 22px",
                  background: "rgba(92,138,92,0.12)",
                  color: TAUPE,
                  border: "1px solid rgba(92,138,92,0.35)",
                }}
              ><div style={{ fontFamily: FF_PLAYFAIR, fontSize: 18, fontWeight: 700, marginBottom: 10 }}>Entreprise</div><p style={{ margin: 0, fontSize: 14, lineHeight: 1.55 }}>
                  Réception OS et mises en demeure, Dépôt photos/documents, Messagerie privée
                </p></div><div
                style={{
                  borderRadius: 14,
                  padding: "20px 22px",
                  background: BEIGE,
                  color: TAUPE,
                  border: `1px solid ${BORDER}`,
                }}
              ><div style={{ fontFamily: FF_PLAYFAIR, fontSize: 18, fontWeight: 700, marginBottom: 10 }}>Client</div><p style={{ margin: 0, fontSize: 14, lineHeight: 1.55 }}>Son projet uniquement, Messagerie, Validation étapes</p></div></div></section></div></div>

      {/* Modal création */}
      {showForm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(61,46,32,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: 20,
            boxSizing: "border-box",
          }}
        ><div
            style={{
              background: BG_CARD,
              borderRadius: 16,
              padding: "32px 36px",
              width: "100%",
              maxWidth: 520,
              boxShadow: "0 24px 70px rgba(0,0,0,0.2)",
              border: `1px solid ${BORDER}`,
            }}
          ><div style={{ fontFamily: FF_PLAYFAIR, fontSize: 22, fontWeight: 700, color: TAUPE, marginBottom: 22 }}>Nouvel utilisateur</div><div style={{ display: "grid", gap: 16 }}><div><label style={labelStyle}>Nom complet *</label><input
                  style={inputStyle}
                  value={form.full_name}
                  onChange={(e) => setForm((p) => ({ ...p, full_name: e.target.value }))}
                  placeholder="Prénom Nom"
                /></div><div><label style={labelStyle}>Adresse email *</label><input
                  style={inputStyle}
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  placeholder="prenom@email.com"
                /></div><div><label style={labelStyle}>Mot de passe * (min. 6 caractères)</label><input
                  style={inputStyle}
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                  placeholder="••••••••"
                /></div><div><label style={labelStyle}>Rôle *</label><select style={{ ...inputStyle, cursor: "pointer" }} value={form.role} onChange={(e) => {
                  const v = e.target.value;
                  setForm((p) => ({
                    ...p,
                    role: v,
                    opc_type: v === "opc" ? (p.opc_type || "mandate") : "mandate",
                  }));
                  if (v !== "opc") setFormOpcProjectIds([]);
                }}>
                  {ROLES.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select></div>
            {form.role === "opc" && (
              <>
                <div>
                  <label style={labelStyle}>Type OPC</label>
                  <div style={{ display: "grid", gap: 10 }}>
                    {OPC_TYPE_OPTIONS.map((opt) => (
                      <label
                        key={opt.value}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 10,
                          cursor: "pointer",
                          fontSize: 14,
                          padding: "10px 12px",
                          borderRadius: 10,
                          border: `1px solid ${form.opc_type === opt.value ? GOLD : BORDER}`,
                          background: form.opc_type === opt.value ? "rgba(192,154,91,0.08)" : BG_CARD,
                        }}
                      >
                        <input
                          type="radio"
                          name="opc_type_create"
                          checked={form.opc_type === opt.value}
                          onChange={() => setForm((p) => ({ ...p, opc_type: opt.value }))}
                          style={{ marginTop: 3 }}
                        />
                        <span>
                          <strong style={{ color: TAUPE }}>{opt.label}</strong>
                          <span style={{ display: "block", fontSize: 12, color: MUTED, marginTop: 4 }}>
                            {opt.hint}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                {form.opc_type === "mandate" && (
                  <div>
                    <label style={labelStyle}>Projets assignés</label>
                    <div
                      style={{
                        maxHeight: 220,
                        overflowY: "auto",
                        border: `1px solid ${BORDER}`,
                        borderRadius: 10,
                        padding: "10px 12px",
                        background: BG_CARD,
                        display: "grid",
                        gap: 8,
                      }}
                    >
                      {allProjects.length === 0 ? (
                        <span style={{ fontSize: 13, color: MUTED }}>Aucun projet dans la base.</span>
                      ) : (
                        allProjects.map((p) => (
                          <label
                            key={p.id}
                            style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 14 }}
                          >
                            <input
                              type="checkbox"
                              checked={formOpcProjectIds.includes(p.id)}
                              onChange={() => toggleFormOpcProject(p.id)}
                            />
                            <span>{p.name || p.id}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                )}
                {form.opc_type === "independant" && (
                  <div
                    style={{
                      padding: "14px 16px",
                      borderRadius: 10,
                      background: "rgba(92,138,92,0.08)",
                      border: "1px solid rgba(92,138,92,0.25)",
                      fontSize: 14,
                      color: TAUPE,
                      lineHeight: 1.55,
                    }}
                  >
                    Cet OPC gère son propre abonnement YOMA
                  </div>
                )}
              </>
            )}
              </div>
            {error && (
              <div
                style={{
                  marginTop: 16,
                  padding: "12px 14px",
                  background: "rgba(184,92,58,0.1)",
                  border: "1px solid rgba(184,92,58,0.35)",
                  borderRadius: 10,
                  fontSize: 13,
                  color: ROUGE,
                  fontFamily: FF_DM,
                }}
              >
                {error}
              </div>
            )}
            <div style={{ display: "flex", gap: 10, marginTop: 24, flexWrap: "wrap" }}><button
                type="button"
                onClick={handleCreate}
                disabled={saving}
                style={{
                  flex: "1 1 160px",
                  padding: "12px 0",
                  background: saving ? "#CCC" : `linear-gradient(135deg, ${GOLD} 0%, #A8844A 100%)`,
                  color: "#FFF",
                  border: "none",
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: saving ? "not-allowed" : "pointer",
                  fontFamily: FF_DM,
                  boxShadow: saving ? "none" : "0 4px 14px rgba(192,154,91,0.4)",
                }}
              >
                {saving ? "Création…" : "Créer le compte"}
              </button><button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setError("");
                  setFormOpcProjectIds([]);
                  setForm({ ...EMPTY_FORM });
                }}
                style={{
                  padding: "12px 20px",
                  background: "transparent",
                  border: `1px solid ${BORDER}`,
                  borderRadius: 10,
                  fontSize: 14,
                  color: MUTED,
                  cursor: "pointer",
                  fontFamily: FF_DM,
                  fontWeight: 600,
                }}
              >
                Annuler
              </button></div></div></div>
      )}

      {opcModalUser && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(61,46,32,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1001,
            padding: 20,
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              background: BG_CARD,
              borderRadius: 16,
              padding: "28px 32px",
              width: "100%",
              maxWidth: 520,
              boxShadow: "0 24px 70px rgba(0,0,0,0.2)",
              border: `1px solid ${BORDER}`,
            }}
          >
            <div style={{ fontFamily: FF_PLAYFAIR, fontSize: 20, fontWeight: 700, color: TAUPE, marginBottom: 6 }}>
              Réglages OPC
            </div>
            <div style={{ fontSize: 13, color: MUTED, marginBottom: 16 }}>
              {opcModalUser.full_name || "—"} · {opcModalUser.email}
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Type OPC</label>
              <select
                style={{ ...inputStyle, cursor: "pointer" }}
                value={opcModalOpcType}
                onChange={(e) => setOpcModalOpcType(e.target.value)}
              >
                {OPC_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label} — {opt.hint}
                  </option>
                ))}
              </select>
            </div>
            {opcModalOpcType === "mandate" && (
              <div
                style={{
                  maxHeight: 280,
                  overflowY: "auto",
                  border: `1px solid ${BORDER}`,
                  borderRadius: 10,
                  padding: "10px 12px",
                  background: BG_CARD,
                  display: "grid",
                  gap: 8,
                  marginBottom: 20,
                }}
              >
                <div style={{ ...labelStyle, marginBottom: 4 }}>Projets assignés</div>
                {allProjects.length === 0 ? (
                  <span style={{ fontSize: 13, color: MUTED }}>Aucun projet dans la base.</span>
                ) : (
                  allProjects.map((p) => (
                    <label
                      key={p.id}
                      style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 14 }}
                    >
                      <input
                        type="checkbox"
                        checked={opcModalIds.includes(p.id)}
                        onChange={() => toggleModalOpc(p.id)}
                      />
                      <span>{p.name || p.id}</span>
                    </label>
                  ))
                )}
              </div>
            )}
            {opcModalOpcType === "independant" && (
              <div
                style={{
                  padding: "14px 16px",
                  borderRadius: 10,
                  background: "rgba(92,138,92,0.08)",
                  border: "1px solid rgba(92,138,92,0.25)",
                  fontSize: 14,
                  color: TAUPE,
                  lineHeight: 1.55,
                  marginBottom: 20,
                }}
              >
                Cet OPC gère son propre abonnement YOMA
              </div>
            )}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={saveOpcModal}
                disabled={savingOpc}
                style={{
                  flex: "1 1 160px",
                  padding: "12px 0",
                  background: savingOpc ? "#CCC" : `linear-gradient(135deg, ${GOLD} 0%, #A8844A 100%)`,
                  color: "#FFF",
                  border: "none",
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: savingOpc ? "not-allowed" : "pointer",
                  fontFamily: FF_DM,
                }}
              >
                {savingOpc ? "Enregistrement…" : "Enregistrer"}
              </button>
              <button
                type="button"
                onClick={() => setOpcModalUser(null)}
                disabled={savingOpc}
                style={{
                  padding: "12px 20px",
                  background: "transparent",
                  border: `1px solid ${BORDER}`,
                  borderRadius: 10,
                  fontSize: 14,
                  color: MUTED,
                  cursor: "pointer",
                  fontFamily: FF_DM,
                  fontWeight: 600,
                }}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal suppression */}
      {confirmDel && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(61,46,32,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: 20,
          }}
        ><div
            style={{
              background: BG_CARD,
              borderRadius: 16,
              padding: "32px 36px",
              width: "100%",
              maxWidth: 400,
              boxShadow: "0 24px 70px rgba(0,0,0,0.2)",
              textAlign: "center",
              border: `1px solid ${BORDER}`,
            }}
          ><div style={{ fontFamily: FF_PLAYFAIR, fontSize: 22, fontWeight: 700, color: TAUPE, marginBottom: 10 }}>Supprimer cet utilisateur ?</div><div style={{ fontSize: 14, color: MUTED, marginBottom: 24, lineHeight: 1.5 }}>
              Le compte de <strong style={{ color: TAUPE }}>{confirmDel.full_name || confirmDel.email}</strong> sera définitivement supprimé.
            </div><div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}><button
                type="button"
                onClick={() => handleDelete(confirmDel.id)}
                style={{
                  flex: 1,
                  padding: "12px 0",
                  background: ROUGE,
                  color: "#FFF",
                  border: "none",
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: FF_DM,
                }}
              >
                Supprimer
              </button><button
                type="button"
                onClick={() => setConfirmDel(null)}
                style={{
                  flex: 1,
                  padding: "12px 0",
                  background: "transparent",
                  border: `1px solid ${BORDER}`,
                  borderRadius: 10,
                  fontSize: 14,
                  color: MUTED,
                  cursor: "pointer",
                  fontFamily: FF_DM,
                  fontWeight: 600,
                }}
              >
                Annuler
              </button></div></div></div>
      )}
    </>
  );
}
